import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const SendMoneyModal = ({ isOpen, onClose, agentId }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    customerId: '',
    recipientId: '',
    sendAmount: '',
    sendCurrency: 'USD',
    receiveAmount: '',
    receiveCurrency: 'EUR',
    purpose: '',
    customerPresent: 'yes'
  });

  // Mock data - replace with actual data
  const customers = [
    { value: 'cust_001', label: 'John Smith - ID: ***4532' },
    { value: 'cust_002', label: 'Maria Garcia - ID: ***7891' },
    { value: 'cust_003', label: 'Ahmed Hassan - ID: ***2156' }
  ];

  const recipients = [
    { value: 'rec_001', label: 'Jane Smith (UK) - ***1234' },
    { value: 'rec_002', label: 'Carlos Garcia (Mexico) - ***5678' },
    { value: 'rec_003', label: 'Fatima Hassan (Morocco) - ***9012' }
  ];

  const currencies = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'MXN', label: 'MXN - Mexican Peso' },
    { value: 'MAD', label: 'MAD - Moroccan Dirham' }
  ];

  const transferPurposes = [
    { value: 'family_support', label: 'Family Support' },
    { value: 'education', label: 'Education' },
    { value: 'medical', label: 'Medical Expenses' },
    { value: 'business', label: 'Business Payment' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate receive amount when send amount changes
    if (field === 'sendAmount' && value) {
      const exchangeRate = 0.92; // Mock rate - replace with actual
      const calculatedAmount = (parseFloat(value) * exchangeRate)?.toFixed(2);
      setFormData(prev => ({ ...prev, receiveAmount: calculatedAmount }));
    }
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    console.log('Submitting transfer:', formData);
    // Handle transfer submission
    onClose?.();
  };

  const getExchangeRate = () => {
    // Mock rate - replace with actual API call
    return '0.92';
  };

  const getTotalFees = () => {
    const amount = parseFloat(formData?.sendAmount) || 0;
    const fee = amount * 0.03; // 3% fee
    return fee?.toFixed(2);
  };

  const getTotalAmount = () => {
    const amount = parseFloat(formData?.sendAmount) || 0;
    const fee = parseFloat(getTotalFees());
    return (amount + fee)?.toFixed(2);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Agent-Assisted Transfer</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Process a money transfer for your customer
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            {[1, 2, 3]?.map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  step === currentStep 
                    ? 'bg-primary text-white' 
                    : step < currentStep 
                    ? 'bg-success text-white' :'bg-muted text-muted-foreground'
                }`}>
                  {step < currentStep ? <Icon name="Check" size={16} /> : step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${
                    step < currentStep ? 'bg-success' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className={`text-xs ${currentStep >= 1 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              Customer Details
            </span>
            <span className={`text-xs ${currentStep >= 2 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              Transfer Details
            </span>
            <span className={`text-xs ${currentStep >= 3 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              Review & Confirm
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Customer Details */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <Select
                label="Select Customer"
                options={customers}
                value={formData?.customerId}
                onChange={(e) => handleInputChange('customerId', e?.target?.value)}
                placeholder="Choose a customer"
                required
              />

              <Select
                label="Select Recipient"
                options={recipients}
                value={formData?.recipientId}
                onChange={(e) => handleInputChange('recipientId', e?.target?.value)}
                placeholder="Choose recipient"
                required
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">Customer Verification</p>
                    <p>Ensure the customer is physically present and has provided valid identification. 
                    Verify their identity against registered information.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Customer Present <span className="text-error">*</span>
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="customerPresent"
                      value="yes"
                      checked={formData?.customerPresent === 'yes'}
                      onChange={(e) => handleInputChange('customerPresent', e?.target?.value)}
                      className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm text-foreground">Yes, customer is present</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="customerPresent"
                      value="no"
                      checked={formData?.customerPresent === 'no'}
                      onChange={(e) => handleInputChange('customerPresent', e?.target?.value)}
                      className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm text-foreground">No, remote transaction</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Transfer Details */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  label="Send Amount"
                  value={formData?.sendAmount}
                  onChange={(e) => handleInputChange('sendAmount', e?.target?.value)}
                  placeholder="0.00"
                  required
                />
                <Select
                  label="Currency"
                  options={currencies}
                  value={formData?.sendCurrency}
                  onChange={(e) => handleInputChange('sendCurrency', e?.target?.value)}
                />
              </div>

              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Icon name="ArrowDown" size={20} className="text-primary" />
                  <span className="text-sm text-muted-foreground">Exchange Rate</span>
                </div>
                <div className="text-2xl font-bold text-foreground">
                  1 {formData?.sendCurrency} = {getExchangeRate()} {formData?.receiveCurrency}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Rate updated 2 minutes ago
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  label="Recipient Receives"
                  value={formData?.receiveAmount}
                  readOnly
                  className="bg-muted"
                />
                <Select
                  label="Currency"
                  options={currencies}
                  value={formData?.receiveCurrency}
                  onChange={(e) => handleInputChange('receiveCurrency', e?.target?.value)}
                />
              </div>

              <Select
                label="Transfer Purpose"
                options={transferPurposes}
                value={formData?.purpose}
                onChange={(e) => handleInputChange('purpose', e?.target?.value)}
                placeholder="Select purpose"
                required
              />

              <div className="bg-muted/20 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Send Amount:</span>
                  <span className="text-foreground font-medium">
                    ${formData?.sendAmount || '0.00'} {formData?.sendCurrency}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Transfer Fee (3%):</span>
                  <span className="text-foreground font-medium">
                    ${getTotalFees()} {formData?.sendCurrency}
                  </span>
                </div>
                <div className="border-t border-border pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-foreground">Total Amount:</span>
                    <span className="font-bold text-foreground text-lg">
                      ${getTotalAmount()} {formData?.sendCurrency}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review & Confirm */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                  <h3 className="font-semibold text-success">Transfer Ready for Processing</h3>
                </div>
                <p className="text-sm text-success/80">
                  Please review all details carefully before confirming the transfer.
                </p>
              </div>

              <div className="space-y-3">
                <div className="bg-card border border-border rounded-lg p-4">
                  <h4 className="font-semibold text-foreground mb-3">Transfer Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Customer:</span>
                      <span className="text-foreground font-medium">
                        {customers?.find(c => c?.value === formData?.customerId)?.label || 'Not selected'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Recipient:</span>
                      <span className="text-foreground font-medium">
                        {recipients?.find(r => r?.value === formData?.recipientId)?.label || 'Not selected'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Send Amount:</span>
                      <span className="text-foreground font-medium">
                        ${formData?.sendAmount || '0.00'} {formData?.sendCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Exchange Rate:</span>
                      <span className="text-foreground font-medium">
                        1 {formData?.sendCurrency} = {getExchangeRate()} {formData?.receiveCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Recipient Receives:</span>
                      <span className="text-foreground font-medium">
                        {formData?.receiveAmount || '0.00'} {formData?.receiveCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transfer Fee:</span>
                      <span className="text-foreground font-medium">
                        ${getTotalFees()} {formData?.sendCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Purpose:</span>
                      <span className="text-foreground font-medium">
                        {transferPurposes?.find(p => p?.value === formData?.purpose)?.label || 'Not specified'}
                      </span>
                    </div>
                    <div className="border-t border-border pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-foreground">Total Charge:</span>
                        <span className="font-bold text-foreground text-lg">
                          ${getTotalAmount()} {formData?.sendCurrency}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                    <div className="text-sm text-warning">
                      <p className="font-medium mb-1">Agent Responsibility</p>
                      <p>By confirming this transfer, you certify that you have verified the customer's 
                      identity and that all information provided is accurate.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? onClose : handleBack}
            iconName={currentStep === 1 ? "X" : "ArrowLeft"}
            iconPosition="left"
          >
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">
              Step {currentStep} of 3
            </span>
            <Button
              onClick={currentStep === 3 ? handleSubmit : handleNext}
              iconName={currentStep === 3 ? "Check" : "ArrowRight"}
              iconPosition="right"
              disabled={
                (currentStep === 1 && (!formData?.customerId || !formData?.recipientId)) ||
                (currentStep === 2 && (!formData?.sendAmount || !formData?.purpose))
              }
            >
              {currentStep === 3 ? 'Confirm Transfer' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendMoneyModal;